import { isForwardSubject } from '@hub/contracts'

/**
 * Where the new text of a plain-text email ends and its history starts (board
 * eeqo). Pure and dependency-free. The markers are what the clients write,
 * in English and Swedish; the HTML twin is `email-quotes-html.ts`.
 */

export interface ForwardHeaders {
  from?: string
  date?: string
  to?: string
  subject?: string
}

export type QuoteKind = 'none' | 'reply' | 'forward'

export interface TextQuoteSplit {
  kind: QuoteKind
  /** What the sender wrote now. */
  body: string
  /** The history: the quote of a reply, the message of a forward. */
  quoted: string
  headers?: ForwardHeaders
}

const SEPARATOR_RE =
  /^-{3,} ?(original message|ursprungligt meddelande|forwarded message|vidarebefordrat meddelande) ?-{3,}$/i
const FORWARD_MARKER_RE = /forwarded|vidarebefordrat/i
const APPLE_FORWARD_RE = /^(begin forwarded message|vidarebefordrat brev):$/i
const ATTRIBUTION_RES = [
  /^(on|den) .+(wrote|skrev):?$/i,
  /^.+ skrev (den )?.+:$/i,
  /^le .+ a écrit ?:$/i,
]
const RULE_RE = /^[_-]{5,}$/

const HEADER_LABELS: ReadonlyArray<[RegExp, keyof ForwardHeaders]> = [
  [/^(?:from|från):\s*(.*)$/i, 'from'],
  [/^(?:sent|skickat|date|datum):\s*(.*)$/i, 'date'],
  [/^(?:to|till):\s*(.*)$/i, 'to'],
  [/^(?:subject|ämne):\s*(.*)$/i, 'subject'],
]

function headerOf(line: string): [keyof ForwardHeaders, string] | null {
  for (const [re, key] of HEADER_LABELS) {
    const match = re.exec(line.trim())
    if (match) return [key, match[1]!.trim()]
  }
  return null
}

const isQuoted = (line: string) => line.trimStart().startsWith('>')
const isBlank = (line: string) => line.trim() === ''

/** Number of consecutive header lines (From:/Sent:/To:/Subject:/Cc:) from `start`. */
function headerRun(lines: string[], start: number): number {
  let end = start
  while (
    end < lines.length &&
    (headerOf(lines[end]!) || /^(cc|kopia):/i.test(lines[end]!.trim()))
  ) {
    end++
  }
  return end - start
}

/** First line of the trailing run of `>` lines, with its attribution. -1 = none. */
function trailingQuoteStart(lines: string[]): number {
  let start = lines.length
  let sawQuote = false
  for (let i = lines.length - 1; i >= 0; i--) {
    if (isQuoted(lines[i]!)) {
      start = i
      sawQuote = true
    } else if (!isBlank(lines[i]!)) {
      break
    }
  }
  if (!sawQuote) return -1
  let above = start - 1
  while (above >= 0 && isBlank(lines[above]!)) above--
  if (above < 0) return start
  const matches = (line: string) => ATTRIBUTION_RES.some((re) => re.test(line.trim()))
  if (matches(lines[above]!)) return above
  // Clients wrap a long attribution over two lines.
  if (above > 0 && matches(`${lines[above - 1]!.trim()} ${lines[above]!.trim()}`)) return above - 1
  return start
}

interface Boundary {
  index: number
  kind: 'reply' | 'forward'
  /** Forwards: where the marker line ends and the header block starts. */
  contentFrom: number
}

function findBoundary(lines: string[], subject: string | null | undefined): Boundary | null {
  const found: Boundary[] = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.trim()
    const separator = SEPARATOR_RE.exec(line)
    if (separator) {
      found.push({
        index: i,
        kind: FORWARD_MARKER_RE.test(separator[1]!) ? 'forward' : 'reply',
        contentFrom: i + 1,
      })
      break
    }
    if (APPLE_FORWARD_RE.test(line)) {
      found.push({ index: i, kind: 'forward', contentFrom: i + 1 })
      break
    }
    if (headerRun(lines, i) >= 3 && headerOf(lines[i]!)) {
      // Outlook draws a rule above the block in plain text too.
      const rule = i > 0 && RULE_RE.test(lines[i - 1]!.trim())
      found.push({
        index: rule ? i - 1 : i,
        kind: isForwardSubject(subject) ? 'forward' : 'reply',
        contentFrom: i,
      })
      break
    }
  }
  const quote = trailingQuoteStart(lines)
  if (quote >= 0) found.push({ index: quote, kind: 'reply', contentFrom: quote })
  return found.sort((a, b) => a.index - b.index)[0] ?? null
}

/** Every From:/Date:/To:/Subject: line of a block of text. */
export function parseHeaderBlock(text: string): ForwardHeaders {
  const headers: ForwardHeaders = {}
  for (const line of text.split('\n')) {
    const header = headerOf(line)
    if (header && header[1] && !headers[header[0]]) headers[header[0]] = header[1]
  }
  return headers
}

/** The header block at the top of a forward, and the message under it. */
function parseForward(lines: string[]): { headers?: ForwardHeaders; rest: string[] } {
  let start = 0
  while (start < lines.length && isBlank(lines[start]!)) start++
  const count = headerRun(lines, start)
  if (count === 0) return { rest: lines }
  const headers = parseHeaderBlock(lines.slice(start, start + count).join('\n'))
  return { headers, rest: lines.slice(start + count) }
}

/** Apple Mail's text part `>`-prefixes the message it forwards. */
function unquote(lines: string[]): string[] {
  const significant = lines.filter((line) => !isBlank(line))
  if (significant.length === 0 || !significant.every(isQuoted)) return lines
  return lines.map((line) => line.replace(/^\s*> ?/, ''))
}

/** Split a text body. `subject` is the email's: it tells a forward from a reply. */
export function splitTextQuote(text: string, subject?: string | null): TextQuoteSplit {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  const boundary = findBoundary(lines, subject)
  if (!boundary) return { kind: 'none', body: text.trim(), quoted: '' }
  const body = lines.slice(0, boundary.index).join('\n').trim()
  if (boundary.kind === 'reply') {
    return { kind: 'reply', body, quoted: lines.slice(boundary.index).join('\n').trim() }
  }
  const { headers, rest } = parseForward(unquote(lines.slice(boundary.contentFrom)))
  return {
    kind: 'forward',
    body,
    quoted: rest.join('\n').trim(),
    ...(headers ? { headers } : {}),
  }
}

// ── Linkify ──────────────────────────────────────────────────────────────────

export type TextToken =
  { type: 'text'; text: string } | { type: 'link'; text: string; href: string }

const LINK_RE = /(?:\bhttps?:\/\/|\bwww\.|\bmailto:)[^\s<>"'`]+/gi
const TRAILING_PUNCTUATION_RE = /[.,;:!?]+$/

function hrefOf(raw: string): string | null {
  const candidate = /^www\./i.test(raw) ? `https://${raw}` : raw
  try {
    const url = new URL(candidate)
    return ['http:', 'https:', 'mailto:'].includes(url.protocol) ? url.href : null
  } catch {
    return null
  }
}

/** A `)` closes the sentence around the URL, unless the URL opened one itself. */
function trimLink(raw: string): string {
  let link = raw
  for (;;) {
    const next = link.replace(TRAILING_PUNCTUATION_RE, '')
    const closes = /[)\]}]$/.test(next)
    const open = next.split(/[([{]/).length - 1
    const close = next.split(/[)\]}]/).length - 1
    const trimmed = closes && close > open ? next.slice(0, -1) : next
    if (trimmed === link) return link
    link = trimmed
  }
}

/** Text → text and link tokens, for React to render: the text is never HTML. */
export function linkifyText(text: string): TextToken[] {
  const tokens: TextToken[] = []
  let last = 0
  for (const match of text.matchAll(LINK_RE)) {
    const link = trimLink(match[0])
    const href = hrefOf(link)
    if (!href) continue
    if (match.index > last) tokens.push({ type: 'text', text: text.slice(last, match.index) })
    tokens.push({ type: 'link', text: link, href })
    last = match.index + link.length
  }
  if (last < text.length) tokens.push({ type: 'text', text: text.slice(last) })
  return tokens
}

/** Consecutive `>` lines become quote blocks; the rest stays text. */
export function segmentQuotedLines(text: string): Array<{ quote: boolean; text: string }> {
  const segments: Array<{ quote: boolean; lines: string[] }> = []
  for (const line of text.split('\n')) {
    const quote = isQuoted(line)
    const current = segments[segments.length - 1]
    if (current && (current.quote === quote || (current.quote && isBlank(line)))) {
      current.lines.push(quote ? line.replace(/^\s*>\s?/, '') : line)
    } else {
      segments.push({ quote, lines: [quote ? line.replace(/^\s*>\s?/, '') : line] })
    }
  }
  return segments.map((s) => ({ quote: s.quote, text: s.lines.join('\n').trim() }))
}
